/**
 * Plays an attack animation on the table: measures the attacker's and victims' cards, asks the
 * picked animation for a plan, and applies its classes and custom properties to the cards'
 * `.card-face` elements (found by `data-card-of`). `cancel` removes everything again.
 */
import type { AttackView } from '@kadice/shared';
import { playSound } from '../audio';
import { pickAttackAnimation, type Box, type Effect } from './attacks';
import './attacks.css';

export interface PlayingAttack {
  /** Milliseconds until the animation has finished. */
  duration: number;
  /** The attacker ends in its own seat in Portrait Layout's column, not back in the big seat. */
  home: boolean;
  /** Removes every class and property the animation added. */
  cancel(): void;
}

const ANIM = '/img/anim';
/** Frames in each one-shot sprite sequence (files are numbered from 1). */
const FRAMES: Record<string, number> = {
  'hits/2': 7,
  'hits/3': 5,
  'hits/4': 7,
  'hits/5': 7,
  'dust/cloud': 11,
  'dust/dash': 8,
};
const FRAME_MS: Record<string, number> = { hit: 55, cloud: 70, dash: 50 };
const frameUrl = (dir: string, n: number) => `${ANIM}/${dir}/${n}.png`;

// Warm the cache so the first impact doesn't flash an empty frame.
if (typeof Image !== 'undefined') {
  for (const [dir, count] of Object.entries(FRAMES)) {
    for (let n = 1; n <= count; n++) new Image().src = frameUrl(dir, n);
  }
  new Image().src = `${ANIM}/attacks/energy_blast.gif`;
}

/** The attacker's seat (and its row) during an attack: over the 'under' sprites (50) and every other seat, under the 'over' sprites (200). */
const ATTACKER_Z = 150;

/** A victim's seat (and its row) while its dice fly off: over every other seat, under the attacker. */
const VICTIM_Z = 120;
/** How long a victim's die takes to fly off the screen. */
const DIE_FLY_MS = 650;
/** How far (radians, either side) the dice of a victim scatter from the way its card is thrown. */
const DIE_SPREAD = 0.8;

const faceOf = (root: ParentNode, id: number) => root.querySelector<HTMLElement>(`[data-card-of="${id}"]`);
/** The dice showing on a player's seat (not the invisible placeholders, empty slots or blank faces). */
const diceOf = (root: ParentNode, id: number) =>
  [...root.querySelectorAll<HTMLElement>(`.seat[data-seat-id="${id}"] > .dice > .die:not(.ghost)`)].filter(
    (el) => el.style.visibility !== 'hidden',
  );

/** Send each die flying off the screen, scattered around the way its victim's card is thrown, spinning. */
function flyOff(dice: HTMLElement[], vw: number, vh: number, flight: { x: number; y: number }): Animation[] {
  const reach = Math.hypot(vw, vh);
  const heading = Math.atan2(flight.y, flight.x);
  return dice.map((die) => {
    const angle = heading + (Math.random() - 0.5) * DIE_SPREAD;
    const spin = (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540);
    const dx = Math.cos(angle) * reach;
    const dy = Math.sin(angle) * reach;
    return die.animate([{ transform: 'none' }, { transform: `translate(${dx}px, ${dy}px) rotate(${spin}deg)` }], {
      duration: DIE_FLY_MS + Math.random() * 200,
      easing: 'cubic-bezier(0.3, 0.6, 0.6, 1)',
      fill: 'forwards',
    });
  });
}

function boxOf(el: HTMLElement): Box {
  return toBox(el.getBoundingClientRect());
}

function toBox(r: { left: number; top: number; width: number; height: number }): Box {
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
  };
}

/**
 * Portrait Layout: where the attacker's power window will be once it is back in its place in
 * the column (now its name row only), measured off another seat in the column (same width, same
 * window under the same name row). Null when there is no such place (Layout v1, wars).
 */
function homeOf(root: ParentNode, id: number): Box | null {
  const slot = root.querySelector<HTMLElement>(`.side-list > .seat.name[data-seat-id="${id}"]`);
  const other = slot?.parentElement?.querySelector<HTMLElement>(':scope > .seat.strip');
  const win = other?.querySelector<HTMLElement>('.strip-window');
  if (!slot || !other || !win) return null;
  const s = slot.getBoundingClientRect();
  const o = other.getBoundingClientRect();
  const w = win.getBoundingClientRect();
  return toBox({ left: s.left + (w.left - o.left), top: s.top + (w.top - o.top), width: w.width, height: w.height });
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Spawn one effect into the fx layers (positions are viewport pixels; layers are table-relative). */
function spawn(fx: Effect, layers: { under: HTMLElement; over: HTMLElement }, origin: DOMRect, timers: number[]) {
  if (fx.kind === 'sound') {
    playSound(fx.sound);
    return;
  }
  const img = document.createElement('img');
  img.className = 'fx';
  img.alt = '';
  const place = (x: number, y: number, angle: number) => {
    img.style.left = `${Math.round(x - origin.left)}px`;
    img.style.top = `${Math.round(y - origin.top)}px`;
    return `translate(-50%, -50%) rotate(${angle}deg)`;
  };
  img.style.width = `${Math.round(fx.size)}px`;

  if (fx.kind === 'blast') {
    img.src = `${ANIM}/attacks/energy_blast.gif`;
    img.style.zIndex = '2';
    img.style.transform = place(fx.x, fx.y, fx.angle);
    layers.over.append(img);
    const base = img.style.transform;
    img.animate([{ transform: base }, { transform: `translate(${fx.tx - fx.x}px, ${fx.ty - fx.y}px) ${base}` }], {
      duration: fx.dur,
      easing: 'linear',
      fill: 'forwards',
    });
    timers.push(window.setTimeout(() => img.remove(), fx.dur));
    return;
  }

  let dir: string;
  let angle = 0;
  let layer = layers.over;
  if (fx.kind === 'hit') {
    dir = `hits/${fx.sets[Math.floor(Math.random() * fx.sets.length)]}`;
    img.style.zIndex = '3';
  } else if (fx.kind === 'cloud') {
    dir = 'dust/cloud';
    layer = layers.under;
  } else {
    dir = 'dust/dash';
    angle = fx.angle;
    img.style.zIndex = '1';
  }
  const count = FRAMES[dir]!;
  const frameMs = FRAME_MS[fx.kind]!;
  img.style.transform = place(fx.x, fx.y, angle);
  img.src = frameUrl(dir, 1);
  layer.append(img);
  for (let n = 2; n <= count; n++)
    timers.push(window.setTimeout(() => (img.src = frameUrl(dir, n)), (n - 1) * frameMs));
  timers.push(window.setTimeout(() => img.remove(), count * frameMs));
}

/**
 * Start the animation for `attack`, or return null if there's nothing to animate (motion off,
 * cards not found). `onImpact` runs at the first impact, the moment a victim is sent flying.
 */
export function playAttack(
  root: HTMLElement,
  attack: AttackView,
  onImpact?: () => void,
  /** The attacker leaves the big seat once this is done: an animation that goes offscreen comes back at its seat's size and lands there. */
  goHome = false,
): PlayingAttack | null {
  if (reducedMotion()) return null;
  const attackerEl = faceOf(root, attack.attackerId);
  const victimEls = attack.victimIds.map((id) => faceOf(root, id)).filter((el): el is HTMLElement => !!el);
  if (!attackerEl || victimEls.length === 0) return null;

  const animation = pickAttackAnimation(attack.attackerId, victimEls.length);
  const home = goHome ? homeOf(root, attack.attackerId) : null;
  const plan = animation.plan({
    attacker: boxOf(attackerEl),
    victims: victimEls.map(boxOf),
    vw: window.innerWidth,
    vh: window.innerHeight,
    home,
  });

  const applied: { el: HTMLElement; classes: string[]; vars: string[] }[] = [];
  const apply = (el: HTMLElement, classes: string[], vars: Record<string, string>) => {
    el.classList.add(...classes);
    for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
    applied.push({ el, classes, vars: Object.keys(vars) });
  };
  apply(attackerEl, ['anim-on', `anim-${animation.id}-attacker`], plan.attacker);
  // The attacker's card is on top of everything but the 'over' sprites. Its own z-index only
  // counts inside its seat (each seat is a stacking context), so every ancestor up to the table
  // is raised too, or a victim's seat later on the page draws over it.
  const raised: { el: HTMLElement; z: string; position: string }[] = [];
  const raise = (from: HTMLElement, z: number) => {
    for (let el = from.parentElement; el && el !== root; el = el.parentElement) {
      if (raised.some((r) => r.el === el)) continue;
      raised.push({ el, z: el.style.zIndex, position: el.style.position });
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.style.zIndex = String(z);
    }
  };
  raise(attackerEl, ATTACKER_Z);
  victimEls.forEach((el, i) => apply(el, ['anim-on', `anim-${animation.id}-victim`], plan.victims[i]!));
  if (plan.shakeAt !== null) {
    root.style.setProperty('--shake-delay', `${Math.round(plan.shakeAt)}ms`);
    root.classList.add('anim-shake');
  }
  document.documentElement.classList.add('anim-playing');

  // Sprite effects live in two layers inside the table, so they shake with it.
  const timers: number[] = [];
  const layers = { under: document.createElement('div'), over: document.createElement('div') };
  layers.under.className = 'fx-layer under';
  layers.over.className = 'fx-layer over';
  root.classList.add('has-fx');
  root.append(layers.under, layers.over);
  const origin = root.getBoundingClientRect();
  for (const fx of plan.effects) {
    timers.push(window.setTimeout(() => spawn(fx, layers, origin, timers), fx.at));
  }
  const hits = plan.effects.filter((fx) => fx.kind === 'hit');
  const firstHit = hits.length > 0 ? Math.min(...hits.map((fx) => fx.at)) : (plan.shakeAt ?? 0);
  if (onImpact) timers.push(window.setTimeout(onImpact, firstHit));

  // Each victim's dice shoot off the screen the moment that victim is hit (the first hit on its card),
  // the way its card is thrown.
  const flying: Animation[] = [];
  let diceEnd = 0;
  attack.victimIds.forEach((id) => {
    const dice = diceOf(root, id);
    const face = faceOf(root, id);
    const flight = plan.flights[victimEls.indexOf(face!)];
    if (dice.length === 0 || !face || !flight) return;
    const b = boxOf(face);
    const on = hits.filter((fx) => Math.abs(fx.x - b.cx) <= b.width && Math.abs(fx.y - b.cy) <= b.height);
    const at = on.length > 0 ? Math.min(...on.map((fx) => fx.at)) : firstHit;
    diceEnd = Math.max(diceEnd, at + DIE_FLY_MS + 200);
    timers.push(
      window.setTimeout(() => {
        raise(dice[0]!, VICTIM_Z);
        flying.push(...flyOff(dice, window.innerWidth, window.innerHeight, flight));
      }, at),
    );
  });

  // The animation isn't over until its last sprite is gone.
  const fxEnd = (fx: Effect) =>
    fx.at +
    (fx.kind === 'sound'
      ? 0
      : fx.kind === 'blast'
        ? fx.dur
        : FRAMES[fx.kind === 'hit' ? 'hits/2' : fx.kind === 'cloud' ? 'dust/cloud' : 'dust/dash']! *
          FRAME_MS[fx.kind]!);
  return {
    duration: Math.max(plan.duration, diceEnd, ...plan.effects.map(fxEnd)),
    home: plan.home,
    cancel() {
      for (const a of flying) a.cancel();
      for (const { el, classes, vars } of applied) {
        el.classList.remove(...classes);
        for (const k of vars) el.style.removeProperty(k);
      }
      for (const { el, z, position } of raised) {
        el.style.zIndex = z;
        el.style.position = position;
      }
      for (const t of timers) clearTimeout(t);
      layers.under.remove();
      layers.over.remove();
      root.classList.remove('has-fx', 'anim-shake');
      root.style.removeProperty('--shake-delay');
      document.documentElement.classList.remove('anim-playing');
    },
  };
}
